import { useEffect, useLayoutEffect, useRef } from 'react';
import { LogoMark } from './Logo';
import { DownloadButton } from './Download';
import { tl } from '../helpers';
import { approve, cancelDraft, editDraft, submit } from '../agent';
import { useStore, setCmd, focusCmd, composer, planEls } from '../store';

const EXAMPLES = [
  ['ABC Endüstri için 50 adet DN50 flanş teklifi hazırla', 'Tek kalem, stok yeterli'],
  ["Çelik Vana'ya 30 adet DN100 flanş ve 200 adet M12 cıvata teklifi çıkar", 'Kısmi stok, kredi limiti uyarısı'],
  ['Doğa Pompa için 120 adet küresel vana, 40 adet kelebek vana ve 60 metre boru teklifi hazırla', 'Üç kalem, hacim iskontosu'],
  ['Kuzey Hidrolik için 80 adet rulman ve 150 adet DN50 conta teklif et', 'İki depo, 30 gün vade']
];

function Steps({ steps }) {
  return (
    <div className="trace">
      {steps.map(s => (
        <div key={s.id} className={'step ' + s.status}>
          <span className="ic"></span>
          <span className="t">{s.title}{s.srcLabel && <span className={'src ' + s.srcCls}>{s.srcLabel}</span>}</span>
          <span className="d num">{s.dur}</span>
          <ul className="subs">{s.subs.map((x, i) => <li key={i} className={x.w ? 'w' : undefined}>{x.t}</li>)}</ul>
        </div>
      ))}
    </div>
  );
}

function Plan({ agent }) {
  const q = agent.plan;
  const dis = agent.planDisabled;
  return (
    <div className="plan" ref={el => { if (el) planEls.set(q.no, el); else planEls.delete(q.no); }}>
      <h3>Teklif taslağı {q.no}</h3>
      <div className="sub">{q.customer.name} · Yetkili: {q.customer.contact} · Geçerlilik {q.validUntil}</div>
      <div className="tbl-wrap">
        <table className="num">
          <thead><tr><th>Ürün</th><th className="r">Miktar</th><th className="r">Birim fiyat</th><th className="r">İsk.</th><th className="r">Tutar</th><th>Stok</th></tr></thead>
          <tbody>
            {q.items.map(i => (
              <tr key={i.code}>
                <td>{i.name}<small>{i.code + ' · ' + i.spec}</small></td>
                <td className="r">{i.qty.toLocaleString('tr-TR') + ' ' + i.unit}</td>
                <td className="r">{tl(i.price)}</td>
                <td className="r">{'%' + i.disc}</td>
                <td className="r">{tl(i.net)}</td>
                <td><span className={'tag ' + (i.stockOk ? 'ok' : 'w')}>{i.stockOk ? 'Yeterli' : 'Kısmi'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="totals num">
        {[['Brüt toplam', tl(q.gross)], ['İskonto', '−' + tl(q.discount)], ['Net toplam', tl(q.net)], ['KDV %20', tl(q.vat)]].map(([k, v]) => (
          [<span key={k}>{k}</span>, <span key={k + 'v'}>{v}</span>]
        ))}
        <span className="g">Genel toplam</span><span className="g">{tl(q.total)}</span>
      </div>
      {q.notes.length > 0 && (
        <div className="notes">
          <b>Dikkat edilmesi gerekenler</b>
          {q.notes.map((x, i) => <div key={i}>{'• ' + x}</div>)}
        </div>
      )}
      <div className="actions">
        <button className="btn primary" disabled={dis} onClick={() => approve(q)}>
          <svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>Onayla ve PDF oluştur
        </button>
        <button className="btn" disabled={dis} onClick={() => editDraft(q)}>
          <svg viewBox="0 0 24 24"><path d="M4 20h4L19 9l-4-4L4 16z" /></svg>Düzenle
        </button>
        <button className="btn" disabled={dis} onClick={() => cancelDraft(q)}>Vazgeç</button>
      </div>
      <div className="guard">
        <svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></svg>
        <span>{agent.guard}</span>
      </div>
    </div>
  );
}

function AgentCard({ agent }) {
  return (
    <div className="agent">
      <div className="agent-head">
        <LogoMark /><b>VİZOVA</b><span className="state">{agent.state}</span><span className="timer num">{agent.timer}</span>
      </div>
      <Steps steps={agent.steps} />
      {agent.ask && (
        <div className="ask">
          <div>{agent.ask.message}</div>
          <div className="opts">
            {agent.ask.options.map(o => (
              <button key={o.label} className="btn" onClick={() => { setCmd(o.value); focusCmd(); }}>{o.label}</button>
            ))}
          </div>
        </div>
      )}
      {agent.plan && <Plan agent={agent} />}
      {agent.steps2 && <Steps steps={agent.steps2} />}
      {agent.result && (
        <div className="result">
          <div className="pdf-ic">PDF</div>
          <div className="meta"><b>{agent.result.filename}</b><span>{agent.result.meta}</span></div>
          <DownloadButton quote={agent.result.quote} disabled={!agent.result.quote.blob} />
        </div>
      )}
      {agent.mail && (
        <details className="mail">
          <summary>{agent.mail.summary}</summary>
          <pre>{agent.mail.body}</pre>
        </details>
      )}
    </div>
  );
}

function Composer() {
  const { cmd, busy } = useStore();
  const ref = useRef(null);
  useEffect(() => { composer.el = ref.current; return () => { composer.el = null; }; }, []);
  useLayoutEffect(() => {
    const t = ref.current;
    t.style.height = 'auto';
    t.style.height = Math.min(t.scrollHeight, 120) + 'px';
  }, [cmd]);
  return (
    <div className="composer">
      <div className="inner">
        <div className="box">
          <textarea ref={ref} id="cmd" rows="1" value={cmd} placeholder="Örn: ABC Endüstri için 50 adet DN50 flanş teklifi hazırla"
            onChange={e => setCmd(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }} />
          <button className="send" aria-label="Gönder" disabled={busy} onClick={submit}><svg viewBox="0 0 24 24"><path d="M4 12h15M13 6l6 6-6 6" /></svg></button>
        </div>
        <div className="hint"><span>Enter ile gönder</span><label><input type="checkbox" checked disabled readOnly /> ERP verilerimi kullan</label><span>Kanal: Web</span></div>
      </div>
    </div>
  );
}

export function ChatView() {
  const { view, chat, scrollTick } = useStore();
  const scroller = useRef(null);
  useLayoutEffect(() => { scroller.current.scrollTop = scroller.current.scrollHeight; }, [scrollTick]);
  return (
    <section className={'view' + (view === 'chat' ? ' active' : '')} id="v-chat">
      <div className="scroll" ref={scroller}>
        <div className="wrap">
          {chat.length === 0 && (
            <div className="intro">
              <h1>Teklif talebini yazın. Stok, fiyat ve iskontoyu VİZOVA toplasın.</h1>
              <p>SAP, Excel ve teklif geçmişinize bakarak taslağı hazırlar. Siz onaylamadan hiçbir kayıt yazılmaz, hiçbir teklif gönderilmez.</p>
              <div className="examples">
                {EXAMPLES.map(([c, s]) => (
                  <button key={c} className="ex" onClick={() => { setCmd(c); focusCmd(); }}>{c}<small>{s}</small></button>
                ))}
              </div>
            </div>
          )}
          {chat.map(c => c.kind === 'user'
            ? [<div key={c.id} className="msg-user"><div>{c.text}</div></div>, <div key={c.id + 'm'} className="msg-meta">{c.meta}</div>]
            : <AgentCard key={c.id} agent={c} />)}
        </div>
      </div>
      <Composer />
    </section>
  );
}
